import { useMemo, useCallback } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { parsePeriodQuery, buildPeriodQuery } from '../helpers/compareHelpers/periodQuery'
import resolveComparePeriods from '../helpers/compareHelpers/resolveComparePeriods'

// AI-CONTEXT: See docs/features/deep-analysis-period.md
//
// The deep-analysis page's period lives in the url query, so a refresh or a
// shared link keeps it. This hook is the page's single reader and writer: it
// parses the query into the value DeepAnalysisPeriodContext hands to every
// section, and writes changes back with `replace` (picking a period is not a
// navigation step) while keeping the page's other params (?compare=, …).

/**
 * @returns {import('../StateManagment/DeepAnalysisPeriodContext').DeepAnalysisPeriod}
 */
const useDeepAnalysisPeriod = () => {
    const { pathname, search } = useLocation()
    const history = useHistory()

    // Keyed on the query string, so the same url hands out the same objects
    // and sections that depend on the period don't refetch on every render.
    const periodValue = useMemo(() => parsePeriodQuery(search), [search])

    const setPeriodValue = useCallback(
        (value) => history.replace({ pathname, search: buildPeriodQuery(value, search) }),
        [history, pathname, search]
    )

    return useMemo(() => ({
        periodValue,
        ...resolveComparePeriods(periodValue),
        setPeriodValue,
    }), [periodValue, setPeriodValue])
}

export default useDeepAnalysisPeriod
